import React, { useState } from "react";
import styled from "styled-components";
import { Player } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import {
  SectionCard,
  InstructionCard,
  PhaseButton,
  MetaInfo,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";
import { ReactComponent as CitizenIcon } from "../../assets/images/user.svg";
import { ReactComponent as MafiaIcon } from "../../assets/images/mafia.svg";
import { ReactComponent as DonIcon } from "../../assets/don_light.svg";
import { ReactComponent as SheriffIcon } from "../../assets/sheriff_light.svg";

interface NightActionsProps {
  language: number;
  players: Player[];
  playersLeaved: number[];
  playerRoles: import("../../types").GameRole[];
  aliveDon: number;
  aliveSheriff: number;
  onComplete: (actions: {
    shooting?: number;
    mafiaTarget: boolean;
    donCheck?: number;
    donMissed: boolean;
    sheriffCheck?: number;
    sheriffMissed: boolean;
  }) => void;
}

type NightStep = "shooting" | "don-check" | "sheriff-check";

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.sm};
  /*margin-bottom: ${theme.spacing.lg};*/
`;

const StepBadge = styled.button.attrs({ type: "button" })<{
  $active?: boolean;
  $variant?: "danger" | "warning" | "info";
}>`
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.round};
  font-weight: 700;
  font-size: 0.9rem;
  border: 1px solid
    ${({ $active, $variant }) => {
      if (!$active) return "rgba(255, 255, 255, 0.18)";
      if ($variant === "danger") return "rgba(255, 96, 122, 0.65)";
      if ($variant === "warning") return "rgba(124, 93, 255, 0.65)";
      return "rgba(255, 196, 0, 0.65)";
    }};
  background: ${({ $active, $variant }) => {
    if (!$active) return "rgba(255, 255, 255, 0.08)";
    if ($variant === "danger")
      return "linear-gradient(135deg, rgba(255, 96, 122, 0.35), rgba(214, 38, 78, 0.55))";
    if ($variant === "warning")
      return "linear-gradient(135deg, rgba(124, 93, 255, 0.35), rgba(70, 46, 150, 0.55))";
    return "linear-gradient(135deg, rgba(255, 196, 0, 0.35), rgba(255, 165, 0, 0.55))";
  }};
  color: ${({ $active }) =>
    $active ? "#ffffff" : "rgba(245, 242, 255, 0.65)"};
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(124, 93, 255, 0.25);
  }
`;

const StepCard = styled(SectionCard)<{
  $variant?: "danger" | "warning" | "info";
}>`
  background: ${({ $variant }) => {
    if ($variant === "danger")
      return "linear-gradient(145deg, rgba(40, 18, 28, 0.96), rgba(28, 12, 20, 0.96))";
    if ($variant === "warning")
      return "linear-gradient(145deg, rgba(28, 20, 40, 0.96), rgba(18, 12, 28, 0.96))";
    return "linear-gradient(145deg, rgba(40, 32, 18, 0.96), rgba(28, 22, 12, 0.96))";
  }};
  border: 1px solid
    ${({ $variant }) => {
      if ($variant === "danger") return "rgba(255, 96, 122, 0.35)";
      if ($variant === "warning") return "rgba(124, 93, 255, 0.35)";
      return "rgba(255, 196, 0, 0.35)";
    }};
  box-shadow: ${({ $variant }) => {
    if ($variant === "danger") return "0 16px 48px rgba(255, 96, 122, 0.15)";
    if ($variant === "warning") return "0 16px 48px rgba(124, 93, 255, 0.15)";
    return "0 16px 48px rgba(255, 196, 0, 0.15)";
  }};
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const StepTitle = styled.h5`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #dcd4ff;
`;

const StepIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  svg,
  img {
    width: 100%;
    height: 100%;
  }
`;

const PlayerGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  justify-content: center;
  margin-bottom: ${theme.spacing.lg};
`;

const PlayerButton = styled.button.attrs({ type: "button" })<{
  $active?: boolean;
  $variant?: "danger" | "warning" | "info";
  $dimmed?: boolean;
}>`
  min-width: 60px;
  padding: 0.75rem 0.5rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 700;
  border: 1px solid
    ${({ $active, $variant }) => {
      if (!$active) {
        if ($variant === "danger") return "rgba(255, 96, 122, 0.35)";
        if ($variant === "warning") return "rgba(124, 93, 255, 0.35)";
        return "rgba(255, 196, 0, 0.35)";
      }
      if ($variant === "danger") return "rgba(255, 96, 122, 0.65)";
      if ($variant === "warning") return "rgba(124, 93, 255, 0.65)";
      return "rgba(255, 196, 0, 0.65)";
    }};
  background: ${({ $active, $variant }) => {
    if (!$active) return "rgba(255, 255, 255, 0.05)";
    if ($variant === "danger")
      return "linear-gradient(135deg, rgba(255, 96, 122, 0.45), rgba(214, 38, 78, 0.65))";
    if ($variant === "warning")
      return "linear-gradient(135deg, rgba(124, 93, 255, 0.45), rgba(70, 46, 150, 0.65))";
    return "linear-gradient(135deg, rgba(255, 196, 0, 0.45), rgba(255, 165, 0, 0.65))";
  }};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.5 : 1)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px
      ${({ $variant }) => {
        if ($variant === "danger") return "rgba(255, 96, 122, 0.3)";
        if ($variant === "warning") return "rgba(124, 93, 255, 0.3)";
        return "rgba(255, 196, 0, 0.3)";
      }};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const PlayerNumber = styled.div`
  font-size: 1rem;
  font-weight: 700;
`;

const PlayerName = styled.div`
  font-size: 0.7rem;
  opacity: 0.85;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RoleIcon = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg,
  img {
    width: 100%;
    height: 100%;
  }
`;

const SpecialButton = styled(PlayerButton)`
  min-width: 80px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
`;

const SkipInfo = styled(InstructionCard)`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(245, 242, 255, 0.75);
  padding: 0.75rem 1rem;
`;

const NightActions: React.FC<NightActionsProps> = ({
  language,
  players,
  playersLeaved,
  playerRoles,
  aliveDon,
  aliveSheriff,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState<NightStep>("shooting");
  const [shooting, setShooting] = useState<number | null>(null);
  const [mafiaTarget, setMafiaTarget] = useState<boolean>(false);
  const [donCheck, setDonCheck] = useState<number | null>(null);
  const [donMissed, setDonMissed] = useState<boolean>(false);
  const [sheriffCheck, setSheriffCheck] = useState<number | null>(null);
  const [sheriffMissed, setSheriffMissed] = useState<boolean>(false);

  const dict = dictionaries[language].ph;

  // Check if don is actually alive by checking both the flag and player status
  const isDonAlive = () => {
    if (aliveDon === 0) return false;
    // Find don in playerRoles and check if they're not in playersLeaved
    const donIndex = playerRoles.findIndex((role) => role === "don");
    if (donIndex === -1) return false;
    return !playersLeaved.includes(donIndex + 1);
  };

  // Check if sheriff is actually alive by checking both the flag and player status
  const isSheriffAlive = () => {
    if (aliveSheriff === 0) return false;
    // Find sheriff in playerRoles and check if they're not in playersLeaved
    const sheriffIndex = playerRoles.findIndex((role) => role === "sheriff");
    if (sheriffIndex === -1) return false;
    return !playersLeaved.includes(sheriffIndex + 1);
  };

  const getAlivePlayers = () => {
    return players.filter((_, index) => !playersLeaved.includes(index + 1));
  };

  const isPlayerAlive = (playerNumber: number) => {
    return !playersLeaved.includes(playerNumber);
  };

  const handleStepClick = (step: NightStep) => {
    setCurrentStep(step);
  };

  const handleShootingSelect = (playerNumber?: number) => {
    if (playerNumber) {
      setShooting(playerNumber);
      setMafiaTarget(false);
    } else {
      setMafiaTarget(true);
      setShooting(null);
    }
    setCurrentStep("don-check");
  };

  const handleDonSelect = (playerNumber?: number) => {
    if (playerNumber) {
      setDonCheck(playerNumber);
      setDonMissed(false);
    } else {
      setDonMissed(true);
      setDonCheck(null);
    }
  };

  const handleSheriffSelect = (playerNumber?: number) => {
    if (playerNumber) {
      setSheriffCheck(playerNumber);
      setSheriffMissed(false);
    } else {
      setSheriffMissed(true);
      setSheriffCheck(null);
    }
  };

  const handleBack = () => {
    if (currentStep === "sheriff-check") {
      setCurrentStep("don-check");
    } else if (currentStep === "don-check") {
      setCurrentStep("shooting");
    }
  };

  const handleComplete = () => {
    onComplete({
      shooting: mafiaTarget ? undefined : shooting || undefined,
      mafiaTarget,
      donCheck: donMissed ? undefined : donCheck || undefined,
      donMissed,
      sheriffCheck: sheriffMissed ? undefined : sheriffCheck || undefined,
      sheriffMissed,
    });
  };

  const canComplete = () => {
    const shootingValid = shooting !== null || mafiaTarget;
    const donValid = !isDonAlive() || donCheck !== null || donMissed;
    const sheriffValid =
      !isSheriffAlive() || sheriffCheck !== null || sheriffMissed;
    return shootingValid && donValid && sheriffValid;
  };

  const getDonCheckIcon = (playerNumber: number): React.ReactNode => {
    const role = playerRoles[playerNumber - 1];
    // Дон видит только шерифа или не шерифа
    return role === "sheriff" ? (
      <SheriffIcon aria-hidden="true" focusable="false" />
    ) : (
      <CitizenIcon aria-hidden="true" focusable="false" />
    );
  };

  const getSheriffCheckIcon = (playerNumber: number): React.ReactNode => {
    const role = playerRoles[playerNumber - 1];
    // Шериф видит мафию (дон или черный) или мирного
    return role === "don" || role === "black" ? (
      <MafiaIcon aria-hidden="true" focusable="false" />
    ) : (
      <CitizenIcon aria-hidden="true" focusable="false" />
    );
  };

  const renderShootingStep = () => (
    <StepCard $variant="danger">
      <StepHeader>
        <StepIcon>
          <MafiaIcon aria-hidden="true" focusable="false" />
        </StepIcon>
        <StepTitle>{dict.stage_1_shooting}</StepTitle>
      </StepHeader>

      <MetaInfo style={{ marginBottom: theme.spacing.lg }}>
        {dict.select_shooting_target}
      </MetaInfo>

      <PlayerGrid>
        {getAlivePlayers().map((player) => {
          const originalIndex = players.findIndex(
            (p) => p.user_id === player.user_id
          );
          const playerNumber = originalIndex + 1;
          const isSelected = shooting === playerNumber;

          return (
            <PlayerButton
              key={player.user_id}
              $active={isSelected}
              $variant="danger"
              onClick={() => handleShootingSelect(playerNumber)}
            >
              <PlayerNumber>{playerNumber}</PlayerNumber>
            </PlayerButton>
          );
        })}

        <SpecialButton
          $active={mafiaTarget}
          $variant="warning"
          onClick={() => handleShootingSelect()}
        >
          <PlayerNumber>{dict.miss}</PlayerNumber>
        </SpecialButton>
      </PlayerGrid>
    </StepCard>
  );

  const renderDonCheckStep = () => (
    <StepCard $variant="warning">
      <StepHeader>
        <StepIcon>
          <DonIcon aria-hidden="true" focusable="false" />
        </StepIcon>
        <StepTitle>{dict.stage_2_don}</StepTitle>
      </StepHeader>

      {!isDonAlive() ? (
        <SkipInfo>{dict.don_dead_skip}</SkipInfo>
      ) : (
        <>
          <MetaInfo
            style={{ marginBottom: theme.spacing.lg }}
          >
            {dict.select_don_check}
          </MetaInfo>

          <PlayerGrid>
            {Array.from({ length: 10 }, (_, i) => {
              const playerNumber = i + 1;
              const isSelected = donCheck === playerNumber;
              const isAlive = isPlayerAlive(playerNumber);

              return (
                <PlayerButton
                  key={playerNumber}
                  $active={isSelected}
                  $variant="warning"
                  $dimmed={!isAlive}
                  onClick={() => handleDonSelect(playerNumber)}
                  title={
                    isAlive
                      ? dict.check_player.replace("{0}", String(playerNumber))
                      : dict.player_dead.replace("{0}", String(playerNumber))
                  }
                >
                  {isSelected ? (
                    <RoleIcon>{getDonCheckIcon(playerNumber)}</RoleIcon>
                  ) : (
                    <PlayerNumber>{playerNumber}</PlayerNumber>
                  )}
                </PlayerButton>
              );
            })}

            <SpecialButton
              $active={donMissed}
              $variant="warning"
              onClick={() => handleDonSelect()}
            >
              <PlayerNumber>{dict.skip}</PlayerNumber>
            </SpecialButton>
          </PlayerGrid>
        </>
      )}

      <ActionButtons>
        <PhaseButton $variant="secondary" onClick={handleBack}>
          {dict.back}
        </PhaseButton>
        {(donCheck !== null || donMissed || !isDonAlive()) && (
          <PhaseButton
            $variant="primary"
            onClick={() => setCurrentStep("sheriff-check")}
          >
            {dict.next}
          </PhaseButton>
        )}
      </ActionButtons>
    </StepCard>
  );

  const renderSheriffCheckStep = () => (
    <StepCard $variant="info">
      <StepHeader>
        <StepIcon>
          <SheriffIcon aria-hidden="true" focusable="false" />
        </StepIcon>
        <StepTitle>{dict.stage_3_sheriff}</StepTitle>
      </StepHeader>

      {!isSheriffAlive() ? (
        <SkipInfo>{dict.sheriff_dead_skip}</SkipInfo>
      ) : (
        <>
          <MetaInfo
            style={{ marginBottom: theme.spacing.lg }}
          >
            {dict.select_sheriff_check}
          </MetaInfo>

          <PlayerGrid>
            {Array.from({ length: 10 }, (_, i) => {
              const playerNumber = i + 1;
              const isSelected = sheriffCheck === playerNumber;
              const isAlive = isPlayerAlive(playerNumber);

              return (
                <PlayerButton
                  key={playerNumber}
                  $active={isSelected}
                  $variant="info"
                  $dimmed={!isAlive}
                  onClick={() => handleSheriffSelect(playerNumber)}
                  title={
                    isAlive
                      ? dict.check_player.replace("{0}", String(playerNumber))
                      : dict.player_dead.replace("{0}", String(playerNumber))
                  }
                >
                  {isSelected ? (
                    <RoleIcon>{getSheriffCheckIcon(playerNumber)}</RoleIcon>
                  ) : (
                    <PlayerNumber>{playerNumber}</PlayerNumber>
                  )}
                </PlayerButton>
              );
            })}

            <SpecialButton
              $active={sheriffMissed}
              $variant="info"
              onClick={() => handleSheriffSelect()}
            >
              <PlayerNumber>{dict.skip}</PlayerNumber>
            </SpecialButton>
          </PlayerGrid>
        </>
      )}

      <ActionButtons>
        <PhaseButton $variant="secondary" onClick={handleBack}>
          {dict.back}
        </PhaseButton>
        <PhaseButton
          $variant="primary"
          onClick={handleComplete}
          disabled={!canComplete()}
        >
          ☀️ {dict.morning}
        </PhaseButton>
      </ActionButtons>
    </StepCard>
  );

  return (
    <ActionsContainer>
      <ProgressBar>
        <StepBadge
          $active={currentStep === "shooting"}
          $variant="danger"
          onClick={() => handleStepClick("shooting")}
        >
          1. {dict.step_shooting}
        </StepBadge>
        <StepBadge
          $active={currentStep === "don-check"}
          $variant="warning"
          onClick={() => handleStepClick("don-check")}
        >
          2. {dict.step_don}
        </StepBadge>
        <StepBadge
          $active={currentStep === "sheriff-check"}
          $variant="info"
          onClick={() => handleStepClick("sheriff-check")}
        >
          3. {dict.step_sheriff}
        </StepBadge>
      </ProgressBar>

      {currentStep === "shooting" && renderShootingStep()}
      {currentStep === "don-check" && renderDonCheckStep()}
      {currentStep === "sheriff-check" && renderSheriffCheckStep()}
    </ActionsContainer>
  );
};

export default NightActions;
